/**
 * Service layer for UI Loader.
 * This service centralizes the state, orchestration, or data access used by its feature area.
 */
import { Injectable, computed, signal } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class UiLoaderService {
  private readonly _activeCount = signal(0);

  readonly loading = computed(() => this._activeCount() > 0);

  show(): void {
    this._activeCount.update((n) => n + 1);
  }

  hide(): void {
    this._activeCount.update((n) => Math.max(0, n - 1));
  }

  async wrap<T>(fn: () => Promise<T>, onError?: (err: unknown) => void): Promise<T | null> {
    this.show();
    try {
      return await fn();
    } catch (err) {
      onError?.(err);
      return null;
    } finally {
      await this.waitForViewToSettle();
      this.hide();
    }
  }

  private waitForViewToSettle(): Promise<void> {
    if (typeof requestAnimationFrame === 'undefined') {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve());
      });
    });
  }
}
